<template>
  <div class="kpi-head">
    <div class="sec">Visão Geral</div>

    <div class="kpi-period-wrap">
      <span class="filter-label">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
        Período
      </span>
      <div class="kpi-period">
        <button
          v-for="opt in periodOptions"
          :key="opt.value"
          type="button"
          class="kpi-period-btn"
          :class="{ active: periodo === opt.value }"
          :disabled="loading"
          @click="$emit('update:periodo', opt.value)"
        >
          {{ opt.label }}
          <span
            v-if="opt.value === 'month' && monthPendingCount"
            class="kpi-period-badge"
            title="Existem conversas pendentes de resolução em semanas anteriores."
          >!</span>
        </button>
      </div>
    </div>
  </div>
  <div class="kpi-row">
    <div class="kpi c-red" :class="{ in: ready }">
      <div class="kpi-top">
        <div>
          <div class="kpi-lbl">Total</div>
          <div v-if="scopeReady === false" class="skel skel-lg"></div>
          <div v-else class="kpi-val" ref="elTotal">{{ displayTotal }}</div>
        </div>
        <div class="kpi-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
      </div>
      <div class="kpi-foot">
        <span v-if="scopeReady === false" class="skel skel-pill"></span>
        <span v-else class="tag neu">Conversas</span>
      </div>
    </div>

    <div class="kpi c-blue" :class="{ in: ready }" style="transition-delay:70ms">
      <div class="kpi-top">
        <div>
          <div class="kpi-lbl">Abertas</div>
          <div v-if="scopeReady === false" class="skel skel-lg"></div>
          <div v-else class="kpi-val">{{ displayOpen }}</div>
        </div>
        <div class="kpi-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
      </div>
      <div class="kpi-foot">
        <span v-if="scopeReady === false" class="skel skel-pill"></span>
        <span v-else class="tag info">Em andamento</span>
      </div>
    </div>

    <div class="kpi c-night" :class="{ in: ready }" style="transition-delay:105ms">
      <div class="kpi-top">
        <div>
          <div class="kpi-lbl">Em Avaliação</div>
          <div v-if="scopeReady === false" class="skel skel-lg"></div>
          <div v-else class="kpi-val">{{ displayEmAvaliacao }}</div>
        </div>
        <div class="kpi-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </div>
      </div>
      <div class="kpi-foot">
        <span v-if="scopeReady === false" class="skel skel-pill"></span>
        <span v-else class="tag night">Aguardando CSAT</span>
      </div>
    </div>

    <div class="kpi c-orange" :class="{ in: ready }" style="transition-delay:140ms">
      <div class="kpi-top">
        <div>
          <div class="kpi-lbl">Sem agente</div>
          <div v-if="scopeReady === false" class="skel skel-lg"></div>
          <div v-else class="kpi-val">{{ displayUnassigned }}</div>
        </div>
        <div class="kpi-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
            <line x1="18" y1="8" x2="23" y2="13"/>
            <line x1="23" y1="8" x2="18" y2="13"/>
          </svg>
        </div>
      </div>
      <div class="kpi-foot">
        <span v-if="scopeReady === false" class="skel skel-pill"></span>
        <span v-else class="tag warn">Precisam atenção</span>
      </div>
    </div>

    <div class="kpi c-green" :class="{ in: ready }" style="transition-delay:210ms">
      <div class="kpi-top">
        <div>
          <div class="kpi-lbl">Resolvidas</div>
          <div v-if="scopeReady === false" class="skel skel-lg"></div>
          <div v-else class="kpi-val">{{ displayResolved }}</div>
        </div>
        <div class="kpi-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
      </div>
      <div class="kpi-foot">
        <span v-if="scopeReady === false" class="skel skel-pill"></span>
        <span v-else class="tag up">{{ resPct }}% resolvidas</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

export type VisaoGeralPeriodo = 'week' | 'month'

const props = defineProps<{
  total: number
  open: number
  emAvaliacao: number
  unassigned: number
  resolved: number
  resPct: number
  periodo: VisaoGeralPeriodo
  loading?: boolean
  // Fase 3 do plano de carregamento progressivo (docs/planos/PLANO-SKELETON-DASHBOARD.md):
  // `undefined`/`true` mostra os números reais (default seguro pra qualquer
  // outro consumidor futuro deste componente); só `false` explícito mostra o
  // skeleton — useDashboardEngine.ts's scopeReady começa `false` e vira
  // `true` uma única vez, sem alternar de novo a cada poll de atualização.
  scopeReady?: boolean
  monthPendingCount?: number
}>()

defineEmits<{
  'update:periodo': [value: VisaoGeralPeriodo]
}>()

const periodOptions: { value: VisaoGeralPeriodo; label: string }[] = [
  { value: 'week',  label: 'Esta semana' },
  { value: 'month', label: 'Mês' },
]

const ready = ref(false)
const displayTotal       = ref(0)
const displayOpen        = ref(0)
const displayEmAvaliacao = ref(0)
const displayUnassigned  = ref(0)
const displayResolved    = ref(0)

function animate(from: number, to: number, setter: (v: number) => void) {
  const dur = 650, start = performance.now()
  const step = (now: number) => {
    const t = Math.min((now - start) / dur, 1)
    const e = 1 - Math.pow(1 - t, 3)
    setter(Math.round(from + (to - from) * e))
    if (t < 1) requestAnimationFrame(step)
    else setter(to)
  }
  requestAnimationFrame(step)
}

watch(() => props.total,       (to, from = 0) => animate(from, to, v => displayTotal.value = v),       { immediate: true })
watch(() => props.open,        (to, from = 0) => animate(from, to, v => displayOpen.value = v),        { immediate: true })
watch(() => props.emAvaliacao, (to, from = 0) => animate(from, to, v => displayEmAvaliacao.value = v), { immediate: true })
watch(() => props.unassigned,  (to, from = 0) => animate(from, to, v => displayUnassigned.value = v),  { immediate: true })
watch(() => props.resolved,    (to, from = 0) => animate(from, to, v => displayResolved.value = v),    { immediate: true })

onMounted(() => {
  setTimeout(() => { ready.value = true }, 50)
})
</script>

<style scoped>
.kpi-head { display: flex; align-items: center; gap: 16px; margin-bottom: 14px; flex-wrap: wrap; }
.kpi-head .sec { flex: 1; margin-bottom: 0; }
.kpi-period-wrap { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.kpi-period { display: flex; gap: 6px; flex-shrink: 0; flex-wrap: wrap; }
.kpi-period-btn {
  padding: 5px 12px; border-radius: 999px; border: 1px solid var(--ink-10);
  background: var(--white); color: var(--ink-60); font-size: 11.5px; font-weight: 600;
  cursor: pointer; transition: background .15s, color .15s, border-color .15s, opacity .15s;
  white-space: nowrap;
}
.kpi-period-btn:hover:not(:disabled) { background: var(--ink-02); }
.kpi-period-btn.active { background: var(--red); border-color: var(--red); color: #fff; }
.kpi-period-btn:disabled { opacity: .5; cursor: not-allowed; }

.kpi-period-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 16px; height: 16px; padding: 0 4px; margin-left: 5px;
  border-radius: 20px; font-size: 9.5px; font-weight: 800;
  background: var(--orange); color: #fff;
  animation: kpi-badge-pulse .9s ease-in-out infinite;
}
.kpi-period-btn.active .kpi-period-badge { background: #fff; color: var(--red); }

@keyframes kpi-badge-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .55; }
}
</style>

